// src/security/Axios.js
import axios from 'axios';
import store from '@/store';
import { authLogout } from '@/store/actions';
import ToastMe from '@/components/ui/ToastMe';
import { jwtDecode } from 'jwt-decode';

/* ───────────────── Debug toggle ───────────────── */
const DEBUG_DEFAULT = true; // flip to false to disable by default
function isDebugOn() {
  try {
    return (
      (typeof window !== 'undefined' && !!window.__DEBUG__) ||
      DEBUG_DEFAULT
    );
  } catch {
    return DEBUG_DEFAULT;
  }
}
function redact(value, keep = 6) {
  if (!value || typeof value !== 'string') return value;
  if (value.length <= keep) return '•'.repeat(value.length);
  return value.slice(0, keep) + '•'.repeat(Math.max(0, value.length - keep));
}
function cloneShallow(obj) {
  try {
    return JSON.parse(JSON.stringify(obj || {}));
  } catch {
    return {};
  }
}

/* ───────────────── Env resolution (Vite/CRA/Next/plain/window) ───────────────── */
function resolveApiBaseUrl() {
  const fromWin =
    (typeof window !== 'undefined' && window.__API_URL__) ||
    undefined;
  if (fromWin) {
    return fromWin.endsWith('/') ? fromWin.slice(0, -1) : fromWin;
  }

  let fromVite;
  try {
    fromVite =
      typeof import.meta !== 'undefined' && import.meta.env
        ? import.meta.env.VITE_API_URL
        : undefined;
  } catch {
    fromVite = undefined;
  }

  const fromCRA =
    (typeof process !== 'undefined' &&
      (process?.env?.REACT_APP_API_URL ||
        process?.env?.REACT_APP_API_BASE_URL)) ||
    undefined;

  const fromNext =
    (typeof process !== 'undefined' &&
      process?.env?.NEXT_PUBLIC_API_URL) ||
    undefined;

  const fromPlain =
    (typeof process !== 'undefined' && process?.env?.API_URL) ||
    undefined;

  const url = fromVite || fromCRA || fromNext || fromPlain || '/api';

  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export const AXIOS_BASE_URL = resolveApiBaseUrl();
const LICENSE_GATE_STORAGE_KEY = 'license_gate_state';

/** Public helper so other modules (fetch, etc.) use the same base URL */
export function getApiBaseUrl() {
  return AXIOS_BASE_URL;
}

/* ───────────────── JWT helpers ───────────────── */
function readJwt() {
  try {
    const tok = localStorage.getItem('jwt_token');
    if (!tok) return null;
    return jwtDecode(tok) || null;
  } catch {
    return null;
  }
}
function jwtDefaultBranch() {
  const d = readJwt();
  return d?.branch_id || null;
}
function jwtDefaultCompany() {
  const d = readJwt();
  return d?.company_id || null;
}

/* ───────────────── Axios defaults ───────────────── */
axios.defaults.baseURL = AXIOS_BASE_URL;
axios.defaults.headers.common['X-localization'] = 'en';

/* Rehydrate persisted headers (and fall back to JWT) */
try {
  // Company
  const persistedCompanyId = localStorage.getItem('company_id');
  const effectiveCompany = persistedCompanyId || jwtDefaultCompany();
  if (effectiveCompany) {
    axios.defaults.headers.common['X-Company-Id'] = effectiveCompany;
    try {
      localStorage.setItem('company_id', effectiveCompany);
    } catch {}
  } else {
    delete axios.defaults.headers.common['X-Company-Id'];
  }

  // Token: prefer customer, else admin
  const persistedCustomer =
    localStorage.getItem('customer_jwt') ||
    localStorage.getItem('customerToken');
  const persistedAdmin = localStorage.getItem('jwt_token');
  const bootToken = persistedCustomer || persistedAdmin;
  if (bootToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }

  // Branch: prefer explicit persisted; else use JWT default
  const persistedBranchId = localStorage.getItem('branch_id'); // can be 'all'
  const defBranch = jwtDefaultBranch();
  const effectiveBranch =
    persistedBranchId && persistedBranchId !== '' && persistedBranchId !== 'null'
      ? persistedBranchId
      : defBranch;

  if (effectiveBranch) {
    axios.defaults.headers.common['X-Branch-Id'] = effectiveBranch;
    try {
      localStorage.setItem('branch_id', effectiveBranch);
    } catch {}
  } else {
    delete axios.defaults.headers.common['X-Branch-Id'];
  }
} catch {}

/* ───────────────── Request/Response DEBUG ───────────────── */
axios.interceptors.request.use(
  (config) => {
    if (isDebugOn()) {
      const safeHeaders = cloneShallow(config.headers || {});
      if (safeHeaders.Authorization)
        safeHeaders.Authorization = redact(safeHeaders.Authorization, 10);
      const urlShown = (config.baseURL || AXIOS_BASE_URL) + (config.url || '');
      console.groupCollapsed(
        `%c[HTTP →] ${String(config.method || 'get').toUpperCase()} ${urlShown}`,
        'color:#0a7'
      );
      console.log('baseURL:', config.baseURL || AXIOS_BASE_URL);
      console.log('url:', config.url);
      console.log('headers:', safeHeaders);
      console.log('X-Company-Id:', safeHeaders['X-Company-Id']);
      console.log('X-Branch-Id :', safeHeaders['X-Branch-Id']);
      const dataPreview = cloneShallow(config.data);
      if (dataPreview?.password)
        dataPreview.password = redact(dataPreview.password, 0);
      console.log('data:', dataPreview);
      console.groupEnd();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    if (isDebugOn()) {
      const cfg = response?.config || {};
      const urlShown = (cfg.baseURL || AXIOS_BASE_URL) + (cfg.url || '');
      console.groupCollapsed(
        `%c[HTTP ←] ${response.status} ${String(cfg.method || 'get').toUpperCase()} ${urlShown}`,
        'color:#07a'
      );
      console.log('status:', response.status, response.statusText);
      console.log('data:', response.data);
      console.groupEnd();
    }
    return response;
  },
  (error) => {
    if (isDebugOn()) {
      const cfg = error?.config || {};
      const urlShown = (cfg.baseURL || AXIOS_BASE_URL) + (cfg.url || '');
      console.groupCollapsed(
        `%c[HTTP ✖] ${error?.response?.status || 'ERR'} ${String(
          cfg.method || 'get'
        ).toUpperCase()} ${urlShown}`,
        'color:#a30'
      );
      console.log('status:', error?.response?.status, error?.response?.statusText);
      console.log('data:', error?.response?.data);
      console.log('headers:', error?.response?.headers);
      console.groupEnd();
    }

    const status = error?.response?.status;
    const url = error?.config?.url || '';

    // Temporarily disabled activation redirect
    // if (
    //   status === 503 &&
    //   (error?.response?.data?.status === 'activation_required' ||
    //     /activation/i.test(error?.response?.data?.message || ''))
    // ) {
    //   if (typeof window !== 'undefined') {
    //     const target = '/activate';
    //     if (!window.location.pathname.startsWith(target)) {
    //       window.location.replace(target);
    //     }
    //   }
    // }

    if (status === 423) {
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(
            LICENSE_GATE_STORAGE_KEY,
            JSON.stringify({
              status: error?.response?.data?.status || 'license_required',
              message:
                error?.response?.data?.message ||
                'A valid license is required before you can continue.',
              blockedUrl: url,
              occurredAt: new Date().toISOString(),
            })
          );
        } catch {}
      }

      store.dispatch(authLogout());

      if (typeof window !== 'undefined') {
        const target = '/license-status';
        if (window.location.pathname !== target) {
          window.location.replace(target);
        }
      }
    }

    if (status === 401) {
      const cfgHeaders = error?.config?.headers || {};
      const hadAuth =
        !!cfgHeaders.Authorization ||
        !!axios.defaults.headers.common?.Authorization;

      const isLogin = /\/auth\/login$/.test(url);
      const isPublic = /\/front\/setting\/data$/.test(url);
      const isSoftGuard =
        /\/order\/(recent|unpaid-credit)$/.test(url) ||
        /\/customer\/[^/]+\/orders$/.test(url);

      if (hadAuth && !isLogin && !isPublic && !isSoftGuard) {
        if (
          localStorage.getItem('jwt_token') ||
          localStorage.getItem('customerToken')
        ) {
          ToastMe(
            error?.response?.data?.message ||
              'Session expired. Please log in again.'
          );
        }
        store.dispatch(authLogout());
      }
    }

    if (status === 451) window.location.replace('/under-construction');
    if (status === 406) window.location.replace('/comingsoon');

    return Promise.reject(error);
  }
);

/* ───────────────── Public Helpers ───────────────── */
export function setBearerToken(token) {
  if (token) {
    try {
      localStorage.setItem('jwt_token', token);
    } catch {}
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try {
      localStorage.removeItem('jwt_token');
    } catch {}
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function setCustomerBearerToken(token) {
  if (token) {
    try {
      localStorage.setItem('customer_jwt', token);
      localStorage.setItem('customerToken', token);
    } catch {}
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try {
      localStorage.removeItem('customer_jwt');
      localStorage.removeItem('customerToken');
    } catch {}
    const adminTok = localStorage.getItem('jwt_token');
    if (adminTok)
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminTok}`;
    else delete axios.defaults.headers.common['Authorization'];
  }
}

export function setCompanyId(id) {
  if (id) {
    try {
      localStorage.setItem('company_id', id);
    } catch {}
    axios.defaults.headers.common['X-Company-Id'] = String(id).trim();
  } else {
    try {
      localStorage.removeItem('company_id');
    } catch {}
    delete axios.defaults.headers.common['X-Company-Id'];
  }
}

/**
 * Always set X-Branch-Id for explicit user choice.
 * - Removes header if: falsy / '' / 'null' / 'undefined'
 * - 'all'|'*' → 'all'
 */
export function setBranchId(id, name) {
  const raw = id != null ? String(id).trim() : '';

  // Clear header + storage if empty/null-ish
  if (!raw || raw === 'null' || raw === 'undefined') {
    try {
      localStorage.removeItem('branch_id');
      localStorage.removeItem('branch_name');
    } catch {}
    delete axios.defaults.headers.common['X-Branch-Id'];
    return;
  }

  // Normalize special "all" scope
  const lower = raw.toLowerCase();
  const finalVal = lower === 'all' || lower === '*' ? 'all' : raw;

  // Persist + set header
  try {
    localStorage.setItem('branch_id', finalVal);
    if (name) localStorage.setItem('branch_name', String(name));
  } catch {}
  axios.defaults.headers.common['X-Branch-Id'] = finalVal;
}

/** Backfill company/branch from JWT **when missing** (use at app boot & after login) */
export function applyTenantFromJwtIfMissing() {
  try {
    const d = readJwt();
    if (!d) return;
    if (d?.company_id && !axios.defaults.headers.common['X-Company-Id']) {
      axios.defaults.headers.common['X-Company-Id'] = d.company_id;
      try {
        localStorage.setItem('company_id', d.company_id);
      } catch {}
    }
    if (
      d?.branch_id &&
      !axios.defaults.headers.common['X-Branch-Id'] &&
      !localStorage.getItem('branch_id')
    ) {
      axios.defaults.headers.common['X-Branch-Id'] = d.branch_id;
      try {
        localStorage.setItem('branch_id', d.branch_id);
      } catch {}
    }
  } catch {}
}

/* Thin wrappers */
export function get(url, options = {}) {
  return axios.get(url, options);
}
export function post(url, data = {}, options = {}) {
  return axios.post(url, data, options);
}
export function head(url, options = {}) {
  return axios.head(url, options);
}
export function patch(url, data = {}, options = {}) {
  return axios.patch(url, data, options);
}
export function put(url, data = {}, options = {}) {
  return axios.put(url, data, options);
}
export function del(url, data = {}, options = {}) {
  return axios.delete(url, { data, ...(options || {}) });
}
